import { getGoogleAnalyticsId, getGTMId } from '../managers/EnvironmentManager';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

class AnalyticsService {
  private gaId: string;
  private gtmId: string;
  private isInitialized: boolean = false;

  constructor() {
    this.gaId = getGoogleAnalyticsId();
    this.gtmId = getGTMId();
  }

  /**
   * Initialize Google Analytics
   */
  initializeGA(): void {
    if (!this.gaId) {
      console.warn('Google Analytics ID not configured');
      return;
    }

    if (this.isInitialized) {
      return;
    }

    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.gaId}`;
    document.head.appendChild(script);

    // Initialize dataLayer and gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer!.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', this.gaId, {
      send_page_view: false // We'll manually track page views
    });

    this.isInitialized = true;
    console.log('Google Analytics initialized:', this.gaId);
  }

  /**
   * Track a page view
   */
  trackPageView(path: string, title?: string): void {
    if (!this.gaId || !window.gtag) {
      return;
    }

    // Use setTimeout to make this non-blocking
    setTimeout(() => {
      try {
        window.gtag!('config', this.gaId, {
          page_path: path,
          page_title: title || document.title
        });

        // Also send to GTM dataLayer
        this.pushToDataLayer({
          event: 'page_view',
          page_path: path,
          page_title: title || document.title
        });
      } catch (error) {
        console.warn('Analytics tracking failed:', error);
      }
    }, 0);
  }

  /**
   * Track a custom event
   */
  trackEvent(eventName: string, eventParams?: Record<string, any>): void {
    if (!this.gaId || !window.gtag) {
      return;
    }

    // Use setTimeout to make this non-blocking
    setTimeout(() => {
      try {
        window.gtag!('event', eventName, eventParams);

        // Also send to GTM dataLayer
        this.pushToDataLayer({
          event: eventName,
          ...eventParams
        });
      } catch (error) {
        console.warn('Analytics event tracking failed:', error);
      }
    }, 0);
  }

  /**
   * Track ecommerce events
   */
  trackPurchase(transactionData: {
    transaction_id: string;
    value: number;
    currency?: string;
    tax?: number;
    shipping?: number;
    items?: any[];
  }): void {
    this.trackEvent('purchase', {
      currency: 'USD',
      ...transactionData
    });
  }

  trackAddToCart(item: {
    item_id: string;
    item_name: string;
    price: number;
    quantity?: number;
  }): void {
    this.trackEvent('add_to_cart', {
      currency: 'USD',
      value: item.price * (item.quantity || 1),
      items: [item]
    });
  }

  trackRemoveFromCart(item: {
    item_id: string;
    item_name: string;
    price: number;
    quantity?: number;
  }): void {
    this.trackEvent('remove_from_cart', {
      currency: 'USD',
      value: item.price * (item.quantity || 1),
      items: [item]
    });
  }

  trackViewItem(item: {
    item_id: string;
    item_name: string;
    price: number;
  }): void {
    this.trackEvent('view_item', {
      currency: 'USD',
      value: item.price,
      items: [item]
    });
  }

  trackBeginCheckout(value: number, items?: any[]): void {
    this.trackEvent('begin_checkout', {
      currency: 'USD',
      value,
      items
    });
  }

  /**
   * Set user ID for tracking
   */
  setUserId(userId: string): void {
    if (!this.gaId || !window.gtag) {
      return;
    }

    // Use setTimeout to make this non-blocking
    setTimeout(() => {
      try {
        window.gtag!('config', this.gaId, {
          user_id: userId
        });

        this.pushToDataLayer({
          user_id: userId
        });
      } catch (error) {
        console.warn('Analytics setUserId failed:', error);
      }
    }, 0);
  }

  /**
   * Set user properties
   */
  setUserProperties(properties: Record<string, any>): void {
    if (!this.gaId || !window.gtag) {
      return;
    }

    // Use setTimeout to make this non-blocking
    setTimeout(() => {
      try {
        window.gtag!('set', 'user_properties', properties);

        this.pushToDataLayer({
          user_properties: properties
        });
      } catch (error) {
        console.warn('Analytics setUserProperties failed:', error);
      }
    }, 0);
  }

  /**
   * Push data to GTM dataLayer
   */
  pushToDataLayer(data: Record<string, any>): void {
    if (!window.dataLayer) {
      return;
    }

    try {
      window.dataLayer.push(data);
    } catch (error) {
      console.warn('Failed to push to dataLayer:', error);
    }
  }

  /**
   * Get GTM ID
   */
  getGTMId(): string {
    return this.gtmId;
  }
}

// Export singleton instance
export default new AnalyticsService();
